import moment from 'moment';
// eslint-disable-next-line @typescript-eslint/naming-convention
import React from 'react';
import type { IFlightLog } from '../../../../common/src/domain/flightLog/IFlightLog';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet, fetchPost } from '../../store/fetch';
import { openApiFetchPost } from '../../store/openApiFetch';
import { appContext } from '../appContext';
import { Modal } from '../common/Modal/Modal';
import {
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelDatePicker,
    EditPanelHeader,
    EditPanelInput,
    EditPanelRow,
    EditPanelSelect,
    EditPanelTextArea,
    EditPanelTime,
    ISelectOption,
} from '../elements';
import {
    AssetsBasedPartialFlightLog,
    Equipment,
    ISurveyDataCommonPart,
    ISurveyDataForAssetsCase,
    ISurveyDataForProjectCase,
    ISurveyDataForSingleCaseWithinDashboardPage,
    ProjectBasedPartialFlightLog,
    SurveyData,
} from './interfaces';

type Nullish<T> = null | undefined | T;
type NullishString = Nullish<string>;
type Props = {
    onClose: () => void;
    onSave: () => void;
    flightLog?: undefined | IFlightLog;
    surveyData: SurveyData;
    equipmentId: string | undefined;
};

type DroneOperator = {
    id: string;
    firstName: string;
    lastName: string;
};

const windowHeightInBatchMode = 420; // here 'px' is the units
const windowHeightInNonBatchMode = 350; // here 'px' is the units

const equipmentTransformer = (item: Equipment) => ({ value: item.id, text: item.name });
const getNonEmptyStringOrNull = (str: NullishString): string | null => (typeof str === 'string' && str !== '' ? str : null);

async function createFlightLog(flightLog: IFlightLog) {
    const url = '/api/flightLog';
    const dataParam = {
        query: { equipmentId: flightLog.equipmentId as string },
        body: { ...flightLog, equipmentId: flightLog.equipmentId as string },
    };

    await openApiFetchPost(url, dataParam.query, dataParam.body);
}

async function updateFlightLog(flightLog: IFlightLog) {
    const dataParam = {
        query: { equipmentId: flightLog.equipmentId as string },
        routeParams: { flightLogId: flightLog.id as string },
        body: flightLog,
    };

    await openApiFetchPost('/api/flightLog/{flightLogId}', { ...dataParam.query, ...dataParam.routeParams }, dataParam.body);
}

async function createProjectBasedFlightLogs(partialFlightLog: ProjectBasedPartialFlightLog): Promise<void> {
    const { projectId, surveyId, equipmentId, remarks, userId } = partialFlightLog;
    const url = `/api/utprojects/${projectId}/batch-flightlogs`;
    const queryParams = { surveyId, projectId, equipmentId };
    const body = { surveyId, equipmentId, remarks, userId };
    await fetchPost(url, queryParams, body);
}

async function createAssetsBasedFlightLogs(partialFlightLog: AssetsBasedPartialFlightLog): Promise<void> {
    const { equipmentId, surveyId } = partialFlightLog;
    const url = `/api/surveys/${surveyId}/flightlogs/from-assets`;
    const queryParams = { surveyId, equipmentId };
    await fetchPost(url, queryParams, partialFlightLog);
}

export function EditFlightLogModal(props: Props) {
    const surveyedDate = props.flightLog?.date ?? (props.surveyData as ISurveyDataForAssetsCase).surveyedOn?.toISOString() ?? '';
    const currentAppContext = React.useContext(appContext.context);
    const translate = useTranslate();
    const [remark, setRemark] = React.useState<NullishString>(props.flightLog?.remarks);
    const [flightDate, setFlightDate] = React.useState<NullishString>(surveyedDate);
    const [flightDuration, setFlightDuration] = React.useState<Nullish<number>>(props.flightLog?.flightDuration);
    const [flightStartTime, setFlightStartTime] = React.useState<NullishString>(props.flightLog?.startTime);
    const [flightEndTime, setFlightEndTime] = React.useState<NullishString>(props.flightLog?.endTime);
    const [droneOperatorId, setDroneOperatorId] = React.useState<NullishString>(props.flightLog?.userId);
    const [selectedEquipmentId, setSelectedEquipmentId] = React.useState<NullishString>(
        (props.surveyData as ISurveyDataForSingleCaseWithinDashboardPage).equipmentId ?? props.equipmentId,
    );
    const [selectedProjectId, setSelectedProjectId] = React.useState<NullishString>(null);
    const [availablePilots, setAvailablePilots] = React.useState<ISelectOption[]>([]);
    const [availableProjects, setAvailableProjects] = React.useState<ISelectOption[]>([]);
    const { projects } = props.surveyData as ISurveyDataForProjectCase;
    const { assetsMetadata } = props.surveyData as ISurveyDataForAssetsCase;
    const isAssetsBasedCase = Array.isArray(assetsMetadata);
    const isProjectBasedCase = Array.isArray(projects);
    const isBatchCreation = isAssetsBasedCase || isProjectBasedCase;
    const windowHeight = isBatchCreation ? windowHeightInBatchMode : windowHeightInNonBatchMode;
    const { equipment } = props.surveyData as ISurveyDataCommonPart;
    const equipmentSelectOptions = equipment?.map(equipmentTransformer);
    const { surveyId } = props.surveyData as ISurveyDataCommonPart;

    React.useEffect(() => {
        const { companyId, isDroneOperator: isUserPilot, userId } = currentAppContext;
        const url = `/api/companies/${companyId}/droneOperators`;

        const loadAndStorePilots = async () => {
            const result: DroneOperator[] = (await fetchGet(url)).json.items;

            setAvailablePilots(
                result.map((pilotObject) => ({
                    text: `${pilotObject.firstName} ${pilotObject.lastName}`,
                    value: pilotObject.id,
                })),
            );

            if (!droneOperatorId && isUserPilot && result.find((i) => i.id === userId)) {
                setDroneOperatorId(userId);
            }
        };

        void loadAndStorePilots();
    }, [droneOperatorId, currentAppContext]);

    React.useEffect(() => {
        if (!projects) {
            return;
        }

        setAvailableProjects(
            projects.map((item) => ({
                value: item.id,
                text: `${item.name}${item.deviceName ? `, ${item.deviceName}` : ''}${item.pilotName ? `, ${item.pilotName}` : ''}`,
            })),
        );
    }, [projects]);

    const isEquipmentIdInvalid = () => selectedEquipmentId === null;
    const isFlightDateInvalid = () => flightDate === null;
    const isFlightDurationInvalid = () => flightDuration === null;
    const isPilotIdInvalid = () => droneOperatorId === null;

    const calcFlightDuration = () => {
        const startTime = moment(flightStartTime, 'HH:mm');
        const endTime = moment(flightEndTime, 'HH:mm');

        if (startTime.isValid() && endTime.isValid()) {
            setFlightDuration(endTime.diff(startTime, 'minutes'));
        }
    };

    const isFieldValid = (fieldName: string, value: Nullish<string | number>, method: (newValue: null) => void): boolean => {
        const el = document.querySelector(`[id^="${fieldName}"][value]`);

        if (el?.hasAttribute('disabled')) {
            return true;
        }

        if (!value) {
            method(null);
        }

        return !!value;
    };

    const isFormValid = () => {
        let isRequiredFieldsValid =
            isFieldValid('date', flightDate, setFlightDate) &&
            isFieldValid('flightDuration', flightDuration, setFlightDuration) &&
            isFieldValid('pilotId', droneOperatorId, setDroneOperatorId);

        if (Array.isArray(equipment)) {
            isRequiredFieldsValid &&= isFieldValid('equipmentId', selectedEquipmentId, setSelectedEquipmentId);
        }

        if (isProjectBasedCase) {
            isRequiredFieldsValid &&= isFieldValid('projectId', selectedProjectId, setSelectedProjectId);
        }

        return isRequiredFieldsValid;
    };

    const handleSaveClicked = async () => {
        if (!isFormValid()) {
            return;
        }

        if (isProjectBasedCase) {
            await createProjectBasedFlightLogs(getPayloadForProjectBasedCase());
        } else if (isAssetsBasedCase) {
            await createAssetsBasedFlightLogs(getPayloadForAssetsBasedCase());
        } else {
            const savePayload = getPayloadForSingleCase();
            if (savePayload.id) {
                await updateFlightLog(savePayload);
            } else {
                await createFlightLog(savePayload);
            }
        }

        props.onSave();
    };

    const projectChangeHandle = (projectId: string) => {
        const selectedProject = availableProjects.find((item) => item.value === projectId);

        if (!selectedProject) {
            return;
        }

        setSelectedProjectId(projectId);
        setRemark(translate('flightLog.projectBasedRemarkStub', selectedProject.text));
    };

    const primaryButtonTitle = isProjectBasedCase || isAssetsBasedCase ? 'common.generate' : 'common.save';

    const modalTitle = (() => {
        let title: string;

        if (isAssetsBasedCase) {
            title = translate('flightLog.generateLogFromVideos');
        } else if (isProjectBasedCase) {
            title = translate('flightLog.generateLogsFromMeasurements');
        } else {
            title = translate('common.addItem', translate('flightLog.entry'));
        }

        return title;
    })();

    return (
        <Modal id="dialog" onClose={() => props.onClose()}>
            <Modal.Title>
                <h3>
                    <span className="panel-title">{modalTitle}</span>
                </h3>
            </Modal.Title>

            <Modal.Content style={{ height: `${windowHeight}px` }}>
                <div className="admin-form" style={{ width: '100%' }}>
                    <EditPanel>
                        {isBatchCreation && (
                            <EditPanelRow>
                                {isProjectBasedCase && (
                                    <EditPanelCell rowCount={10}>
                                        <EditPanelSelect
                                            name="projectId"
                                            options={availableProjects}
                                            value={selectedProjectId}
                                            handleChange={projectChangeHandle}
                                            placeholder={translate('flightLog.selectProjectPlaceholder')}
                                            topLabel={translate('flightLog.selectProjectLabel')}
                                        />
                                    </EditPanelCell>
                                )}
                            </EditPanelRow>
                        )}

                        <EditPanelRow>
                            <EditPanelCell rowCount={4}>
                                <EditPanelSelect
                                    name="pilotId"
                                    value={droneOperatorId}
                                    handleChange={setDroneOperatorId}
                                    topLabel={translate('flightLog.droneOperator')}
                                    placeholder={translate('flightLog.selectPilotPlaceholder')}
                                    options={availablePilots}
                                    notValid={isPilotIdInvalid()}
                                />
                                {isPilotIdInvalid() && <em className="state-error">{translate('flightLog.pilotIsRequired')}</em>}
                            </EditPanelCell>

                            {Array.isArray(equipment) && (
                                <EditPanelCell rowCount={4}>
                                    <EditPanelSelect
                                        name="equipmentId"
                                        value={selectedEquipmentId}
                                        handleChange={setSelectedEquipmentId}
                                        topLabel={translate('flightLog.selectEquipmentLabel')}
                                        placeholder={translate('flightLog.selectEquipmentPlaceholder')}
                                        options={equipmentSelectOptions}
                                        notValid={isEquipmentIdInvalid()}
                                    />
                                    {isEquipmentIdInvalid() && <em className="state-error">{translate('flightLog.equipmentIsRequired')}</em>}
                                </EditPanelCell>
                            )}

                            <EditPanelCell rowCount={4}>
                                <EditPanelDatePicker
                                    topLabel={translate('flightLog.flightDateLabel')}
                                    name="date"
                                    value={flightDate}
                                    handleChange={setFlightDate}
                                    notValid={isFlightDateInvalid()}
                                    disabled={isProjectBasedCase}
                                />
                                {isFlightDateInvalid() && <em className="state-error">{translate('flightLog.flightDateRequired')}</em>}
                            </EditPanelCell>
                        </EditPanelRow>

                        <EditPanelHeader text={translate('flightLog.duration')} />

                        <EditPanelRow>
                            <EditPanelCell rowCount={4}>
                                <EditPanelTime
                                    topLabel={translate('common.start')}
                                    name="startTime"
                                    value={flightStartTime}
                                    handleChange={setFlightStartTime}
                                    handleBlur={calcFlightDuration}
                                    disabled={isProjectBasedCase || isAssetsBasedCase}
                                />
                            </EditPanelCell>

                            <EditPanelCell rowCount={4}>
                                <EditPanelTime
                                    topLabel={translate('common.end')}
                                    name="endTime"
                                    value={flightEndTime}
                                    handleChange={setFlightEndTime}
                                    handleBlur={() => calcFlightDuration()}
                                    disabled={isProjectBasedCase || isAssetsBasedCase}
                                />
                            </EditPanelCell>

                            <EditPanelCell rowCount={4}>
                                <EditPanelInput
                                    name="flightDuration"
                                    topLabel={translate('flightLog.minutes')}
                                    placeholder={translate('common.total')}
                                    value={flightDuration}
                                    handleChange={(value) => setFlightDuration(parseInt(value, 10))}
                                    notValid={isFlightDurationInvalid()}
                                    disabled={isProjectBasedCase || isAssetsBasedCase}
                                />
                                {isFlightDurationInvalid() && <em className="state-error">{translate('flightLog.flightDurationRequired')}</em>}
                            </EditPanelCell>
                        </EditPanelRow>

                        <EditPanelTextArea disabled={isAssetsBasedCase} value={remark} handleChange={(value) => setRemark(value)} name="remark" />
                    </EditPanel>
                </div>
            </Modal.Content>

            <Modal.Actions>
                <EditPanelButtons fixWidth={true}>
                    <EditPanelButton small={true} text={translate('common.cancel')} onClick={() => props.onClose()} />

                    <EditPanelButton small={true} text={translate(primaryButtonTitle)} onClick={handleSaveClicked} primary />
                </EditPanelButtons>
            </Modal.Actions>
        </Modal>
    );

    function getPayloadForSingleCase(): IFlightLog {
        if (!selectedEquipmentId) {
            throw new Error('selectedEquipmentId is required');
        }
        return {
            equipmentId: selectedEquipmentId,
            surveyId: props.flightLog?.surveyId ?? surveyId,
            id: props.flightLog?.id,
            date: flightDate as string,
            flightDuration: flightDuration as number,
            startTime: getNonEmptyStringOrNull(flightStartTime),
            endTime: getNonEmptyStringOrNull(flightEndTime),
            remarks: getNonEmptyStringOrNull(remark),
            userId: droneOperatorId as string,
        };
    }

    function getPayloadForProjectBasedCase(): ProjectBasedPartialFlightLog {
        if (!selectedEquipmentId) {
            throw new Error('selectedEquipmentId is required');
        }
        return {
            projectId: selectedProjectId as string,
            equipmentId: selectedEquipmentId,
            surveyId,
            remarks: remark as string,
            userId: droneOperatorId as string,
        };
    }

    function getPayloadForAssetsBasedCase(): AssetsBasedPartialFlightLog {
        if (!selectedEquipmentId) {
            throw new Error('selectedEquipmentId is required');
        }
        return {
            date: flightDate as string,
            equipmentId: selectedEquipmentId,
            surveyId,
            userId: droneOperatorId as string,
        };
    }
}
